import api from '@/api/axiosApi'
import {
  AssortmentPlan,
  AssortmentPlansFilters,
  AssortmentPlansResponse,
  ApiPlanSummary,
  ApiPlanStatus,
  ApiHierarchySummary,
  PaginatedPlansResponse,
  PlanStatus,
  ApiPlanDetailResponse,
} from '@/types'
import { formatUserDisplayName } from '@/utils/formatters'

// Mapping functions from API format to frontend display format

function mapApiStatusToDisplay(apiStatus: ApiPlanStatus): PlanStatus {
  switch (apiStatus) {
    case 'DRAFT':
      return 'Draft'
    case 'IN_PROGRESS':
      return 'In Progress'
    case 'SUBMITTED':
      return 'Submitted'
    case 'APPROVED':
      return 'Approved'
    case 'REJECTED':
      return 'Rejected'
    case 'SENT_BACK':
      return 'Sent Back'
    default:
      return 'Draft'
  }
}

function buildHierarchyPath(hierarchy?: ApiHierarchySummary): string {
  if (!hierarchy) return '-'

  const levels = ['DIVISION', 'DEPARTMENT', 'CLASS', 'SUBCLASS'] as const
  const parts: string[] = []

  // Add lineage items in order
  if (hierarchy.lineage) {
    for (const level of levels) {
      const item = hierarchy.lineage.find(l => l.level === level)
      if (item) parts.push(item.name)
    }
  }

  // Add current node if its level comes after lineage
  if (hierarchy.level && hierarchy.name) {
    parts.push(hierarchy.name)
  }

  return parts.length > 0 ? parts.join(' / ') : '-'
}

function mapApiPlanToFrontend(apiPlan: ApiPlanSummary): AssortmentPlan {
  return {
    id: apiPlan.id,
    name: apiPlan.name,
    hierarchy: buildHierarchyPath(apiPlan.hierarchy),
    hierarchy_id: apiPlan.hierarchy?.id ?? null,
    versions_count: apiPlan.versions_count ?? 0,
    owner: formatUserDisplayName(apiPlan.owner),
    owner_id: apiPlan.owner?.id ?? null,
    status: mapApiStatusToDisplay(apiPlan.status ?? 'DRAFT'),
    active_version_id: apiPlan.active_version_id ?? null,
    active_version_alias: apiPlan.active_version_alias ?? '',
  }
}

// Map frontend filter values to API format
export function mapStatusToApi(status: string): string | undefined {
  if (!status || status === 'all') return undefined
  switch (status) {
    case 'Draft':
      return 'DRAFT'
    case 'In Progress':
      return 'IN_PROGRESS'
    case 'Submitted':
      return 'SUBMITTED'
    case 'Approved':
      return 'APPROVED'
    case 'Rejected':
      return 'REJECTED'
    case 'Sent Back':
      return 'SENT_BACK'
    default:
      return undefined
  }
}

export async function fetchAssortmentPlans(
  filters: AssortmentPlansFilters = {}
): Promise<AssortmentPlansResponse> {
  const {
    search,
    status,
    owner_id,
    hierarchy_id,
    season_id,
    page = 1,
    pageSize = 20,
  } = filters

  // Convert page/pageSize to offset/limit
  const offset = (page - 1) * pageSize
  const limit = pageSize

  // Build query params
  const params: Record<string, string | number> = {
    offset,
    limit,
    sort: '-modified_at', // Newest first
  }

  // Add search query
  if (search && search.trim() !== '') {
    params.q = search.trim()
  }

  // Add status filter
  const apiStatus = mapStatusToApi(status ?? '')
  if (apiStatus) {
    params.status = apiStatus
  }

  // Add owner filter
  if (owner_id) {
    params.owner_id = owner_id
  }

  // Add hierarchy filter
  if (hierarchy_id) {
    params.hierarchy_id = hierarchy_id
  }

  // Add season filter
  if (season_id) {
    params.season_id = season_id
  }

  const response = await api.get<PaginatedPlansResponse>('/plans', { params })

  const { data: items, pagination } = response.data

  // Map API plans to frontend format
  const plans = items.map(mapApiPlanToFrontend)

  // Calculate pagination info
  const totalCount = pagination.total
  const totalPages = Math.ceil(totalCount / pageSize)

  return {
    plans,
    totalCount,
    totalPages,
    currentPage: page,
  }
}

// Delete a plan
export async function deletePlan(planId: string): Promise<void> {
  await api.delete(`/plans/${planId}`)
}

// Copy a plan
export interface CopyPlanParams {
  name: string
  sourceVersionId: string
  hierarchyId?: string | null
}

export async function copyPlan(params: CopyPlanParams): Promise<void> {
  await api.post('/plans', {
    name: params.name,
    plan_type: 'CORE', // All plans are now Core
    source_version_id: params.sourceVersionId,
    created_from: 'COPY',
    hierarchy_id: params.hierarchyId ?? undefined,
  })
}

// Update plan name
export async function updatePlanName(
  planId: string,
  name: string
): Promise<void> {
  await api.patch(`/plans/${planId}`, { name })
}

// Update plan header fields (generic partial update)
export async function updatePlanHeader(
  planId: string,
  updates: Record<string, string | number | string[] | null>
): Promise<ApiPlanDetailResponse> {
  const response = await api.patch<ApiPlanDetailResponse>(
    `/plans/${planId}`,
    updates
  )
  return response.data
}
